import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Upload, Image, Trash2, Loader2, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const MediaPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const fileInputRef = useRef(null);

  const fetchMedia = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/media/`, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      setMedia(response.data);
    } catch (error) {
      toast.error('Failed to load media');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchMedia();
  }, [fetchMedia]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check if it's an image
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', file.name);

    try {
      await axios.post(`${API}/media/upload`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true,
      });
      toast.success('Media uploaded!');
      fetchMedia();
    } catch (error) {
      toast.error('Failed to upload media');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async (mediaId) => {
    try {
      await axios.delete(`${API}/media/${mediaId}`, {
        headers: getAuthHeaders(),
        withCredentials: true,
      });
      toast.success('Media deleted');
      setSelectedMedia(null);
      fetchMedia();
    } catch (error) {
      toast.error('Failed to delete media');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="min-h-screen bg-background page-with-bottom-nav" data-testid="media-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 pt-24 pb-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-serif text-3xl font-bold">Media Gallery</h1>
            <p className="text-muted-foreground mt-1">
              Share and browse images from the community
            </p>
          </div>
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleUpload}
              className="hidden"
              id="media-upload"
            />
            <Button
              className="btn-primary"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="upload-media-btn"
            >
              {uploading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Upload className="h-4 w-4 mr-2" />
              )}
              Upload Image
            </Button>
          </div>
        </div>

        {/* Media Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : media.length === 0 ? (
          <Card className="card-default text-center py-12">
            <Image className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No media uploaded yet. Be the first!</p>
          </Card>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {media.map((item, index) => (
              <Card
                key={item.media_id}
                className="card-default overflow-hidden cursor-pointer group animate-slide-up"
                style={{ animationDelay: `${index * 0.03}s` }}
                onClick={() => setSelectedMedia(item)}
                data-testid={`media-${item.media_id}`}
              >
                <div className="aspect-square bg-muted relative">
                  <img
                    src={`${API}/media/${item.media_id}`}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <p className="text-white text-sm font-medium px-2 text-center truncate">
                      {item.title}
                    </p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Media Detail Dialog */}
        <Dialog open={!!selectedMedia} onOpenChange={() => setSelectedMedia(null)}>
          <DialogContent className="max-w-3xl">
            {selectedMedia && (
              <>
                <DialogHeader>
                  <DialogTitle className="font-serif">{selectedMedia.title}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="rounded-lg overflow-hidden bg-muted">
                    <img
                      src={`${API}/media/${selectedMedia.media_id}`}
                      alt={selectedMedia.title}
                      className="w-full max-h-[60vh] object-contain"
                    />
                  </div>
                  <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <div>
                      <p>Uploaded by: {selectedMedia.uploader_name}</p>
                      <p>Size: {formatFileSize(selectedMedia.size)}</p>
                    </div>
                    {(selectedMedia.uploaded_by === user?.user_id || user?.role === 'admin') && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(selectedMedia.media_id)}
                        data-testid={`delete-media-${selectedMedia.media_id}`}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </Button>
                    )}
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default MediaPage;
